// Gated room impulse response, generated at load from a room's reverb setting (shared/audio.ts GatedReverb). No DOM:
// returns plain Float32Arrays so tests and offline checks run the same code.
//
// t = 0 is the direct sound's arrival at the listener (the direct sound itself is the dry path, not in the IR).
// 1. Early reflections: image sources of the room box (all orders) from the source to the listener, at their delays,
//    amplitude 1/distance, the per-band energy decay exp(-13.8 t / RT60) at their travel time, and the specular part
//    (1 - scattering)^order. Panned left/right by the image's direction, the listener facing -Z (three.js forward).
//    They fade out between 60 and 100 ms (raised cosine).
// 2. Diffuse tail: white noise (another seed per channel, so the channels are decorrelated) with the statistical
//    energy rate of the room (4 pi c / V, scaled to match the image sources' measured rate) and the per-band decay;
//    it carries the scattered energy while the image sources play and all of it after they fade.
// 3. Bands: each of the 7 octave bands is the band's signal through LP(upper edge) - LP(lower edge), 2nd-order
//    Butterworth low-passes; with equal envelopes the bands sum back to the input exactly.
// 4. Gate: 1 until hold_ms, then a raised cosine to 0 over release_ms; the IR ends at hold + release, last sample 0.

import { REVERB_BANDS_HZ, type GatedReverb } from '@lr3d/shared';

type V3 = [number, number, number];

const C = 343;
/** Image sources fade out over this window (s after the direct sound). */
const ISM_FADE: [number, number] = [0.06, 0.1];
/** Band edges between the octave bands (geometric means). */
const EDGES = REVERB_BANDS_HZ.slice(1).map((f) => f / Math.SQRT2);

export interface RoomIR {
  channels: [Float32Array, Float32Array];
  sampleRate: number;
  /** Image sources used, and the noise level calibration (image-source rate / statistical rate). */
  images: number;
  calibration: number;
}

/** IR length in seconds. */
export function gatedLength(spec: GatedReverb): number {
  return (spec.hold_ms + spec.release_ms) / 1000;
}

/** The gate's gain at time t (s after the direct sound). */
export function gateGain(spec: GatedReverb, t: number): number {
  const hold = spec.hold_ms / 1000, rel = spec.release_ms / 1000;
  if (t <= hold) return 1;
  if (t >= hold + rel) return 0;
  return 0.5 * (1 + Math.cos((Math.PI * (t - hold)) / rel));
}

function fadeOut(t: number, [a, b]: [number, number]): number {
  if (t <= a) return 1;
  if (t >= b) return 0;
  return 0.5 * (1 + Math.cos((Math.PI * (t - a)) / (b - a)));
}

/**
 * The gated IR for `spec` with the sound coming from `source` (clamped into the room box: a source in a ceiling shaft
 * enters the room at the opening).
 */
export function gatedReverbIR(spec: GatedReverb, source: V3, sampleRate: number, seed = 1): RoomIR {
  const fs = sampleRate;
  const n = Math.round(gatedLength(spec) * fs) + 1;
  const { min, max } = spec.room;
  const L = [0, 1, 2].map((i) => max[i]! - min[i]!);
  const V = L[0]! * L[1]! * L[2]!;
  const S = 2 * (L[0]! * L[1]! + L[0]! * L[2]! + L[1]! * L[2]!);
  const scat = spec.scattering ?? 0.2;
  const rt = spec.rt60_s;
  const nb = rt.length;
  const src = [0, 1, 2].map((i) => Math.min(max[i]! - 1e-3, Math.max(min[i]! + 1e-3, source[i]!)) - min[i]!);
  const lis = [0, 1, 2].map((i) => spec.listener[i]! - min[i]!);
  const d0 = Math.hypot(src[0]! - lis[0]!, src[1]! - lis[1]!, src[2]! - lis[2]!);
  const t0 = d0 / C; // travel time of the direct sound

  // per band, per channel: the band's signal before its band filter
  const bands = Array.from({ length: nb }, () => [new Float32Array(n), new Float32Array(n)] as [Float32Array, Float32Array]);

  // 1. image sources: along each axis, positions 2kL + s (order 2|k|) and 2kL - s (order |2k - 1|)
  const tMax = ISM_FADE[1];
  const rMax = C * (tMax + t0);
  const axis = (i: number) => {
    const out: { p: number; o: number }[] = [];
    const K = Math.ceil(rMax / (2 * L[i]!)) + 1;
    for (let k = -K; k <= K; k++) {
      out.push({ p: 2 * k * L[i]! + src[i]!, o: Math.abs(2 * k) });
      out.push({ p: 2 * k * L[i]! - src[i]!, o: Math.abs(2 * k - 1) });
    }
    return out;
  };
  const ax = axis(0), ay = axis(1), az = axis(2);
  let images = 0;
  let ismEnergy = 0; // specular-free energy in the calibration window, band 0 decay removed
  const calA = 0.035, calB = 0.06;
  for (const x of ax) for (const y of ay) for (const z of az) {
    const dx = x.p - lis[0]!, dy = y.p - lis[1]!, dz = z.p - lis[2]!;
    const d = Math.hypot(dx, dy, dz);
    if (d > rMax) continue;
    const t = (d - d0) / C;
    if (t < 1e-4) continue; // the direct sound (and an image that coincides with it: a source on a surface)
    const order = x.o + y.o + z.o;
    const amp = 1 / d;
    if (t >= calA && t < calB) ismEnergy += amp * amp;
    const w = fadeOut(t, ISM_FADE);
    if (w <= 0) continue;
    images++;
    const spec = Math.sqrt(Math.pow(1 - scat, order));
    const pan = dx / d; // listener faces -Z: +X is the right ear
    const gl = Math.sqrt((1 - pan) / 2), gr = Math.sqrt((1 + pan) / 2);
    const pos = t * fs;
    const i0 = Math.floor(pos), fr = pos - i0;
    if (i0 + 1 >= n) continue;
    const tAbs = t + t0;
    for (let b = 0; b < nb; b++) {
      const a = amp * spec * w * Math.exp((-6.9078 * tAbs) / rt[b]!);
      const [l, r] = bands[b]!;
      l[i0]! += a * gl * (1 - fr); l[i0 + 1]! += a * gl * fr;
      r[i0]! += a * gr * (1 - fr); r[i0 + 1]! += a * gr * fr;
    }
  }

  // 2. diffuse tail. Statistical energy rate (1/r amplitude units, per second, before decay): 4 pi c / V. The image
  //    sources' own rate in the calibration window sets the scale (a source on a surface doubles it).
  const statRate = (4 * Math.PI * C) / V;
  const calibration = Math.min(4, Math.max(0.25, ismEnergy / (calB - calA) / statRate || 1));
  const rate = statRate * calibration;
  const reflPerS = (C * S) / (4 * V);
  // the first reflection's time: the tail starts there with a 5 ms raised-cosine rise
  const firstT = firstReflection(ax, ay, az, lis, d0, rMax);
  for (let ch = 0; ch < 2; ch++) {
    let s = (seed * 7919 + ch * 104729 + 1) % 2147483647 || 1;
    for (let i = 0; i < n; i++) {
      s = (s * 16807) % 2147483647;
      // uniform noise, unit variance
      const white = (s / 2147483647 - 0.5) * Math.sqrt(12);
      const t = i / fs;
      if (t < firstT) continue;
      const rise = t < firstT + 0.005 ? 0.5 * (1 - Math.cos((Math.PI * (t - firstT)) / 0.005)) : 1;
      const tAbs = t + t0;
      const specular = fadeOut(t, ISM_FADE) * Math.pow(1 - scat, tAbs * reflPerS);
      const base = (rate / fs) * Math.max(0, 1 - specular) * rise;
      for (let b = 0; b < nb; b++) bands[b]![ch]![i]! += white * Math.sqrt(base * Math.exp((-13.8155 * tAbs) / rt[b]!));
    }
  }

  // 3. band filters, sum; 4. gate
  const out: [Float32Array, Float32Array] = [new Float32Array(n), new Float32Array(n)];
  for (let b = 0; b < nb; b++) {
    const hi = b < nb - 1 ? EDGES[b]! : null;
    const lo = b > 0 ? EDGES[b - 1]! : null;
    for (let ch = 0; ch < 2; ch++) {
      const x = bands[b]![ch]!;
      const up = hi ? lowpass(x, hi, fs) : x;
      const down = lo ? lowpass(x, lo, fs) : null;
      const o = out[ch]!;
      for (let i = 0; i < n; i++) o[i]! += up[i]! - (down ? down[i]! : 0);
    }
  }
  let peak = 0;
  for (const o of out) for (let i = 0; i < n; i++) { o[i]! *= gateGain(spec, i / fs); peak = Math.max(peak, Math.abs(o[i]!)); }
  // level: peak 0.5 (the ConvolverNode normalizes the IR's power anyway)
  if (peak > 0) for (const o of out) for (let i = 0; i < n; i++) o[i]! *= 0.5 / peak;
  out[0][n - 1] = 0; out[1][n - 1] = 0;
  return { channels: out, sampleRate: fs, images, calibration };
}

function firstReflection(ax: { p: number }[], ay: { p: number }[], az: { p: number }[], lis: number[], d0: number, rMax: number): number {
  let best = Infinity;
  for (const x of ax) for (const y of ay) for (const z of az) {
    const d = Math.hypot(x.p - lis[0]!, y.p - lis[1]!, z.p - lis[2]!);
    if (d > rMax) continue;
    const t = (d - d0) / C;
    if (t >= 1e-4 && t < best) best = t;
  }
  return Number.isFinite(best) ? best : 0;
}

/** 2nd-order Butterworth low-pass (RBJ biquad, bilinear), applied forward. */
export function lowpass(x: Float32Array, f: number, fs: number): Float32Array {
  const w = (2 * Math.PI * Math.min(f, fs * 0.45)) / fs;
  const cw = Math.cos(w), alpha = Math.sin(w) / (2 * Math.SQRT1_2);
  const a0 = 1 + alpha;
  const b0 = (1 - cw) / 2 / a0, b1 = (1 - cw) / a0, b2 = b0, a1 = (-2 * cw) / a0, a2 = (1 - alpha) / a0;
  const y = new Float32Array(x.length);
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
  for (let i = 0; i < x.length; i++) {
    const v = b0 * x[i]! + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
    x2 = x1; x1 = x[i]!; y2 = y1; y1 = v;
    y[i] = v;
  }
  return y;
}
